import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { lumi } from '../lib/lumi'
import {X, Upload, Loader2, ImageIcon, Search, Check} from 'lucide-react'
import toast from 'react-hot-toast'

interface MediaPickerModalProps {
  open: boolean
  onClose: () => void
  onSelect: (url: string) => void
  currentUrl?: string
  title?: string
}

/** Collections + fields scanned to build the media library */
const MEDIA_SOURCES: { collection: string; fields: string[] }[] = [
  { collection: 'artwork', fields: ['image'] },
  { collection: 'events', fields: ['image', 'magazineCoverImage', 'magazineArticleImage'] },
  { collection: 'site_content', fields: ['image', 'content.image'] },
  { collection: 'home_content', fields: ['image', 'content.image', 'content.backgroundImage'] },
]

interface MediaItem {
  url: string
  source: string
}

const getByPath = (obj: any, path: string): any => {
  if (!obj) return undefined
  return path.split('.').reduce((acc, k) => (acc == null ? acc : acc[k]), obj)
}

const MediaPickerModal = ({
  open,
  onClose,
  onSelect,
  currentUrl,
  title = 'Media library',
}: MediaPickerModalProps) => {
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [items, setItems] = useState<MediaItem[]>([])
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<string | null>(currentUrl || null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!open) return
    setSelected(currentUrl || null)
    loadMedia()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  const loadMedia = async () => {
    setLoading(true)
    try {
      const results = await Promise.all(
        MEDIA_SOURCES.map(async (src) => {
          try {
            const res = await (lumi.entities as any)[src.collection]?.list?.({ limit: 500 })
            const list: any[] = res?.list || []
            const urls: MediaItem[] = []
            list.forEach((doc) => {
              src.fields.forEach((f) => {
                const v = getByPath(doc, f)
                if (typeof v === 'string' && v.startsWith('http')) {
                  urls.push({ url: v, source: src.collection })
                }
              })
            })
            return urls
          } catch {
            return [] as MediaItem[]
          }
        })
      )

      // Flatten + dedupe by url
      const map = new Map<string, MediaItem>()
      results.flat().forEach((it) => {
        if (!map.has(it.url)) map.set(it.url, it)
      })
      setItems(Array.from(map.values()))
    } catch (err) {
      console.error('Media load error:', err)
      toast.error('Could not load the media library')
    } finally {
      setLoading(false)
    }
  }

  const handleUploadClick = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const fileName = file.name?.toLowerCase() || ''
    const isHeic = file.type === 'image/heic' || file.type === 'image/heif' || fileName.endsWith('.heic') || fileName.endsWith('.heif')
    if (isHeic) {
      toast.error('Format HEIC non supporté. Convertissez l’image en JPG ou PNG.')
      if (fileInputRef.current) fileInputRef.current.value = ''
      return
    }
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image')
      return
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image is too large (max 10MB)')
      return
    }
    setUploading(true)
    try {
      const results = await lumi.tools.file.upload([file])
      const result = results?.[0]
      if (!result?.fileUrl) throw new Error(result?.uploadError || 'Upload failed')
      const newUrl = result.fileUrl
      setItems((prev) => [{ url: newUrl, source: 'upload' }, ...prev.filter((i) => i.url !== newUrl)])
      setSelected(newUrl)
      toast.success('Image uploaded')
    } catch (err: any) {
      console.error('Upload error:', err)
      toast.error(err?.message || 'Upload failed')
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const filteredItems = useMemo(() => {
    if (!query.trim()) return items
    const q = query.toLowerCase()
    return items.filter(
      (it) => it.url.toLowerCase().includes(q) || it.source.toLowerCase().includes(q)
    )
  }, [items, query])

  const handleConfirm = () => {
    if (!selected) {
      toast.error('Please select an image')
      return
    }
    onSelect(selected)
    onClose()
  }

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <ImageIcon size={20} className="text-[#7F1734]" />
            <h2 className="text-lg font-semibold text-[#2C2C2C]">{title}</h2>
            <span className="text-xs text-gray-500">
              {loading ? '...' : `${items.length} image${items.length > 1 ? 's' : ''}`}
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 transition"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row gap-3 px-6 py-3 border-b border-gray-200 bg-gray-50">
          <div className="flex-1 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search images..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#7F1734]"
            />
          </div>
          <button
            onClick={handleUploadClick}
            disabled={uploading}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-[#7F1734] text-white text-sm font-medium rounded-lg hover:bg-[#6a132c] transition disabled:opacity-60"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload size={16} />
                Upload a new image
              </>
            )}
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-gray-500">
              <Loader2 size={24} className="animate-spin mr-2" />
              Loading media library...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-gray-500">
              <ImageIcon size={40} className="mb-3 opacity-40" />
              <p className="text-sm">
                {query ? 'No image matches your search.' : 'No image in the library yet.'}
              </p>
              <button
                onClick={handleUploadClick}
                className="mt-4 text-sm text-[#7F1734] underline"
              >
                Upload your first image
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredItems.map((item) => {
                const isSelected = selected === item.url
                return (
                  <button
                    key={item.url}
                    onClick={() => setSelected(item.url)}
                    className={`relative group aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 transition-all ${
                      isSelected
                        ? 'border-[#7F1734] ring-2 ring-[#7F1734]/30'
                        : 'border-transparent hover:border-gray-300'
                    }`}
                    title={item.url}
                  >
                    <img
                      src={item.url}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover"
                    />
                    <span className="absolute bottom-1 left-1 right-1 text-[10px] px-1.5 py-0.5 bg-black/60 text-white rounded truncate text-left">
                      {item.source}
                    </span>
                    {isSelected && (
                      <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#7F1734] text-white flex items-center justify-center shadow">
                        <Check size={14} />
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          <p className="text-xs text-gray-500 truncate max-w-[60%]">
            {selected ? selected : 'Select an image or upload a new one'}
          </p>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 rounded-lg transition"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selected}
              className="px-4 py-2 bg-[#7F1734] text-white text-sm font-medium rounded-lg hover:bg-[#6a132c] transition disabled:opacity-50"
            >
              Use this image
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

export default MediaPickerModal
